<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import instagramClient from 'dashboard/api/channel/instagramClient';
import { setupFacebookSdk, initInstagramLogin } from './instagram/utils';

const router = useRouter();
const { t } = useI18n();

const { fbAppId, fbApiVersion, instagramConfigurationId, instagramAppId } =
  window.chatwootConfig;

// Without INSTAGRAM_CONFIGURATION_ID the Instagram OAuth flow stays the default
const hasMetaBusinessLogin = Boolean(fbAppId && instagramConfigurationId);
const hasInstagramLogin = Boolean(instagramAppId);

const isRequestingAuthorization = ref(false);
const hasError = ref(false);
const errorStateMessage = ref('');
const errorStateDescription = ref('');

// Instagram::CallbacksController redirects here with OAuth errors
onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const errorCode = urlParams.get('code');
  const errorMessage = urlParams.get('error_message');

  if (errorMessage) {
    hasError.value = true;
    if (errorCode === '400') {
      errorStateMessage.value = errorMessage;
      errorStateDescription.value = t('INBOX_MGMT.ADD.INSTAGRAM.ERROR_AUTH');
    } else {
      errorStateMessage.value = t('INBOX_MGMT.ADD.INSTAGRAM.ERROR_MESSAGE');
      errorStateDescription.value = errorMessage;
    }
  }
  // Remove the error params so a reload lets the user try again
  window.history.replaceState({}, document.title, window.location.pathname);
});

const requestInstagramAuthorization = async () => {
  isRequestingAuthorization.value = true;
  try {
    const {
      data: { url },
    } = await instagramClient.generateAuthorization();
    window.location.href = url;
  } finally {
    isRequestingAuthorization.value = false;
  }
};

const requestMetaBusinessAuthorization = async () => {
  isRequestingAuthorization.value = true;
  hasError.value = false;

  try {
    await setupFacebookSdk(fbAppId, fbApiVersion);
    const code = await initInstagramLogin(instagramConfigurationId);
    const { data } = await instagramClient.embeddedSignup({ code });

    useAlert(t('INBOX_MGMT.FINISH.MESSAGE'));
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: data.id },
    });
  } catch (error) {
    if (error.message === 'Login cancelled') {
      useAlert(t('INBOX_MGMT.ADD.INSTAGRAM.CANCELLED'));
    } else {
      hasError.value = true;
      errorStateMessage.value = t('INBOX_MGMT.ADD.INSTAGRAM.ERROR_MESSAGE');
      errorStateDescription.value =
        error.response?.data?.error || error.message || '';
    }
  } finally {
    isRequestingAuthorization.value = false;
  }
};

const requestAuthorization = () =>
  hasMetaBusinessLogin
    ? requestMetaBusinessAuthorization()
    : requestInstagramAuthorization();
</script>

<template>
  <div class="h-full p-6 w-full max-w-full flex-shrink-0 flex-grow-0">
    <div class="flex flex-col items-center justify-start h-full text-center">
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
        />
      </div>
      <div
        v-else
        class="flex flex-col items-center justify-center px-8 py-10 text-center rounded-2xl outline outline-1 outline-n-weak"
      >
        <h6 class="text-2xl font-medium">
          {{ $t('INBOX_MGMT.ADD.INSTAGRAM.CONNECT_YOUR_INSTAGRAM_PROFILE') }}
        </h6>
        <p class="py-6 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.INSTAGRAM.HELP') }}
        </p>
        <button
          class="flex items-center justify-center px-8 py-3.5 gap-2 text-white rounded-full bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] hover:shadow-lg transition-all duration-300 min-w-[240px] overflow-hidden"
          :disabled="isRequestingAuthorization"
          @click="requestAuthorization()"
        >
          <span class="i-ri-instagram-line size-5" />
          <span class="text-base font-medium">
            {{ $t('INBOX_MGMT.ADD.INSTAGRAM.CONTINUE_WITH_INSTAGRAM') }}
          </span>
          <span v-if="isRequestingAuthorization" class="ml-2">
            <svg
              class="w-5 h-5 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </span>
        </button>
        <button
          v-if="hasMetaBusinessLogin && hasInstagramLogin"
          class="mt-4 text-sm underline text-n-slate-11 hover:text-n-slate-12"
          :disabled="isRequestingAuthorization"
          @click="requestInstagramAuthorization()"
        >
          {{ $t('INBOX_MGMT.ADD.INSTAGRAM.USE_INSTAGRAM_LOGIN') }}
        </button>
      </div>
    </div>
  </div>
</template>
